import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";
import { Users, MousePointer, TrendingUp, Calendar, ExternalLink, Eye } from "lucide-react";
import api from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_LABELS = {
  "request":   { label: "Customer Request Form", emoji: "📝", color: "#16a34a" },
  "track":     { label: "Job Tracking Portal",   emoji: "🔍", color: "#2563eb" },
  "qr-poster": { label: "QR Poster Page",        emoji: "🖨️",  color: "#7c3aed" },
};

const StatCard = ({ title, value, icon: Icon, color, sub, loading }) => (
  <Card className="rounded-2xl border border-slate-200/70 bg-white">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center`} style={{ background: `${color}18` }}>
          <Icon size={17} style={{ color }} />
        </div>
      </div>
      {loading ? <Skeleton className="h-8 w-20" /> : (
        <>
          <p className="text-2xl font-bold tabular-nums text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </>
      )}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-600">{PAGE_LABELS[p.dataKey]?.label || p.dataKey}:</span>
          <span className="font-bold text-slate-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [range,   setRange]   = useState(7); // 7 or 30 days

  useEffect(() => {
    api.get(`${API}/analytics/summary`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const chartData = data?.chart_data?.slice(range === 7 ? -7 : -30) || [];
  const totalVisitors = data?.total_all ?? 0;
  const todayVisitors = data?.total_today ?? 0;
  const weekVisitors  = data?.total_week ?? 0;

  // Page breakdown stats
  const pages = Object.entries(PAGE_LABELS).map(([key, info]) => ({
    key,
    ...info,
    total: data?.page_totals?.[key] ?? 0,
    today: data?.today_totals?.[key] ?? 0,
    week:  data?.week_totals?.[key]  ?? 0,
  }));

  // Most visited page
  const topPage = pages.reduce((a, b) => (a.total > b.total ? a : b), pages[0] || {});

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Visitor Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            See how many customers are visiting your public pages — like Shopify
          </p>
        </div>
        <div className="flex gap-2">
          {[7, 30].map(d => (
            <button key={d}
              onClick={() => setRange(d)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                range === d
                  ? "bg-green-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>
              Last {d} days
            </button>
          ))}
        </div>
      </div>

      {/* Top KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Visitors" value={totalVisitors.toLocaleString()} icon={Users}        color="#16a34a" sub="All time"       loading={loading} />
        <StatCard title="Today"          value={todayVisitors}                  icon={Eye}          color="#2563eb" sub="Visits today"   loading={loading} />
        <StatCard title="This Week"      value={weekVisitors}                   icon={TrendingUp}   color="#7c3aed" sub="Last 7 days"    loading={loading} />
        <StatCard title="Most Visited"   value={loading ? "…" : (topPage.emoji + " " + (topPage.label?.split(" ")[0] || "—"))} icon={MousePointer} color="#ea580c" sub={`${topPage.total ?? 0} visits`} loading={loading} />
      </div>

      {/* Trend chart */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
              Daily Visitor Trend
            </h2>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {Object.entries(PAGE_LABELS).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: v.color }} />
                  {v.emoji} {v.label.split(" ")[0]}
                </span>
              ))}
            </div>
          </div>
          {loading ? (
            <Skeleton className="h-48 w-full rounded-xl" />
          ) : chartData.length === 0 || chartData.every(d => d.request + d.track + d["qr-poster"] === 0) ? (
            <div className="h-48 flex flex-col items-center justify-center text-slate-400">
              <Users size={32} className="mb-2 opacity-30" />
              <p className="text-sm">No visitor data yet</p>
              <p className="text-xs mt-1">Share your links and data will appear here</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                {Object.entries(PAGE_LABELS).map(([key, info]) => (
                  <Bar key={key} dataKey={key} stackId="a" fill={info.color} radius={key === "qr-poster" ? [4,4,0,0] : [0,0,0,0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Per-page breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        {pages.map(p => (
          <Card key={p.key} className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: `${p.color}15` }}>
                  {p.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{p.label}</p>
                  <p className="text-xs text-slate-400 font-mono">/{p.key}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Today", p.today], ["Week", p.week], ["Total", p.total]].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-xs text-slate-400">{l}</p>
                    <p className="text-lg font-bold tabular-nums" style={{ color: p.color, fontFamily: "'Space Grotesk',sans-serif" }}>{v}</p>
                  </div>
                ))}
              </div>
              <a href={`/${p.key}`} target="_blank" rel="noreferrer"
                className="mt-3 flex items-center justify-center gap-1 text-xs border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-700 py-1.5 rounded-lg transition-colors">
                <ExternalLink size={11} /> Open page
              </a>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick share links */}
      <Card className="rounded-2xl border border-green-200/70 bg-green-50/40">
        <CardContent className="p-5">
          <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">📤 Share These Links to Get More Visitors</p>
          <div className="space-y-2">
            {pages.map(p => (
              <div key={p.key} className="flex items-center gap-3 p-2.5 bg-white rounded-xl border border-slate-200">
                <span className="text-base flex-shrink-0">{p.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700">{p.label}</p>
                  <p className="text-xs text-green-600 font-mono truncate">
                    {window.location.origin}/{p.key}
                  </p>
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/${p.key}`); }}
                  className="text-xs text-green-700 hover:text-green-800 border border-green-200 px-2 py-1 rounded-lg hover:bg-green-50 flex-shrink-0">
                  Copy
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
